/**
 * @zendir/ui - Toast / Notification System
 *
 * Contextual toast notifications with queue management, auto-dismiss,
 * and Astro UX status colors. Essential for operator dashboards surfacing
 * connection events, command results, and system alerts.
 *
 * Astro UX Compliance:
 * - 6-level status colors (off, standby, normal, caution, serious, critical)
 * - Positioned top-right (operator dashboard convention)
 * - Reduced motion support
 * - ARIA live region for screen readers
 *
 * @example
 * ```tsx
 * // Wrap app in ToastProvider
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // Use in any component
 * const toast = useToast();
 * toast({ status: 'normal', title: 'Connected', message: 'MQTT link established' });
 * toast({ status: 'caution', title: 'SAFE Mode', message: 'Spacecraft entered SAFE mode', duration: 0 });
 * toast({ status: 'critical', title: 'Link Lost', message: 'Downlink signal below threshold' });
 * ```
 */

import React, {
  memo,
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useTheme } from "../../theme";
import { safeAccentText } from "../../utils";
import { useBreakpoint } from "../layout/useBreakpoint";

// ─── Types ───────────────────────────────────────────────────────────────────

export type ToastStatus =
  | "normal"
  | "standby"
  | "caution"
  | "serious"
  | "critical"
  | "off";
export type ToastPosition =
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left"
  | "top-center"
  | "bottom-center";

export interface ToastOptions {
  /** Status level (determines color) */
  status?: ToastStatus;
  /** Title text (bold) */
  title: string;
  /** Description text */
  message?: string;
  /** Auto-dismiss duration in ms (0 = persistent, default 5000) */
  duration?: number;
  /** Dismissible by user click (default true) */
  dismissible?: boolean;
  /** Icon override */
  icon?: React.ReactNode;
  /** Action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastInstance extends ToastOptions {
  id: string;
  createdAt: number;
}

// ─── Context ─────────────────────────────────────────────────────────────────

type ToastFn = (options: ToastOptions) => string;

interface ToastContextValue {
  toast: ToastFn;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Hook to show toast notifications.
 * Must be used within a `<ToastProvider>`.
 *
 * @example
 * ```tsx
 * const toast = useToast();
 * toast({ status: 'normal', title: 'Success', message: 'Command sent' });
 * ```
 */
export function useToast(): ToastFn {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx.toast;
}

/**
 * Hook to access full toast API (toast, dismiss, dismissAll).
 */
export function useToastManager(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx)
    throw new Error("useToastManager must be used within <ToastProvider>");
  return ctx;
}

// ─── Toast Item ──────────────────────────────────────────────────────────────

const ToastItem = memo(function ToastItem({
  toast: t,
  onDismiss,
}: {
  toast: ToastInstance;
  onDismiss: (id: string) => void;
}): React.ReactElement {
  const { tokens } = useTheme();
  const [exiting, setExiting] = useState(false);
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup dismiss timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(dismissTimeoutRef.current);
    };
  }, []);

  const statusColors: Record<string, string> = {
    normal: tokens.colors.status.normal,
    standby: tokens.colors.status.standby,
    caution: tokens.colors.status.caution,
    serious: tokens.colors.status.serious,
    critical: tokens.colors.status.critical,
    off: tokens.colors.status.off,
  };

  const color = statusColors[t.status || "off"] || tokens.colors.status.off;

  const handleDismiss = useCallback(() => {
    setExiting(true);
    dismissTimeoutRef.current = setTimeout(() => onDismiss(t.id), 200);
  }, [t.id, onDismiss]);

  // Auto-dismiss
  useEffect(() => {
    const dur = t.duration ?? 5000;
    if (dur <= 0) return;
    const timer = setTimeout(handleDismiss, dur);
    return () => clearTimeout(timer);
  }, [t.duration, handleDismiss]);

  // Status icon — Astro UX dual-coded shapes
  const statusIcon = (() => {
    const s = 10;
    const h = s / 2;
    const status = t.status || "off";
    const sharedStyle: React.CSSProperties = {
      flexShrink: 0,
      marginTop: "3px",
    };
    switch (status) {
      case "critical":
        return (
          <svg
            width={s}
            height={s}
            viewBox={`0 0 ${s} ${s}`}
            style={sharedStyle}
          >
            <polygon points={`${h},${s} 0,0 ${s},0`} fill={color} />
          </svg>
        );
      case "serious":
        return (
          <svg
            width={s}
            height={s}
            viewBox={`0 0 ${s} ${s}`}
            style={sharedStyle}
          >
            <polygon
              points={`${h},0 ${s},${h} ${h},${s} 0,${h}`}
              fill={color}
            />
          </svg>
        );
      case "caution":
        return (
          <svg
            width={s}
            height={s}
            viewBox={`0 0 ${s} ${s}`}
            style={sharedStyle}
          >
            <rect width={s} height={s} fill={color} />
          </svg>
        );
      case "standby":
        return (
          <svg width={s} height={s} viewBox="0 0 12 12" style={sharedStyle}>
            <circle
              cx="6"
              cy="6"
              r="3.5"
              fill="none"
              stroke={color}
              strokeWidth="2"
            />
          </svg>
        );
      case "off":
        return (
          <svg width={s} height={s} viewBox="0 0 12 12" style={sharedStyle}>
            <circle cx="6" cy="6" r="3" fill={color} />
          </svg>
        );
      default: // normal
        return (
          <svg
            width={s}
            height={s}
            viewBox={`0 0 ${s} ${s}`}
            style={sharedStyle}
          >
            <circle cx={h} cy={h} r={h} fill={color} />
          </svg>
        );
    }
  })();

  return (
    <div
      role="alert"
      style={{
        display: "flex",
        gap: tokens.spacing.sm,
        padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
        backgroundColor: tokens.colors.background.elevated,
        border: `1px solid ${color}30`,
        borderLeft: `3px solid ${color}`,
        borderRadius: tokens.borderRadius.md,
        boxShadow: `${tokens.shadows.lg}, 0 0 20px ${color}15`,
        maxWidth: "min(400px, calc(100vw - 32px))",
        minWidth: "min(280px, calc(100vw - 32px))",
        fontFamily: tokens.typography.fontFamily.primary,
        animation: exiting
          ? `zendir-toast-exit 200ms ${tokens.animation.easing.default} forwards`
          : `zendir-toast-enter 300ms ${tokens.animation.easing.default}`,
        transition: tokens.animation.normal,
      }}
    >
      {t.icon || statusIcon}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.semibold,
            color: tokens.colors.text.primary,
            lineHeight: tokens.typography.lineHeight.tight,
          }}
        >
          {t.title}
        </div>
        {t.message && (
          <div
            style={{
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.text.secondary,
              marginTop: "2px",
              lineHeight: tokens.typography.lineHeight.normal,
            }}
          >
            {t.message}
          </div>
        )}
        {t.action && (
          <button
            onClick={t.action.onClick}
            style={{
              marginTop: tokens.spacing.xs,
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: tokens.typography.fontWeight.semibold,
              color: safeAccentText(tokens.colors.accent.primary),
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: tokens.typography.fontFamily.primary,
            }}
          >
            {t.action.label}
          </button>
        )}
      </div>

      {t.dismissible !== false && (
        <button
          aria-label="Dismiss notification"
          onClick={handleDismiss}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            border: "none",
            background: "none",
            color: tokens.colors.text.tertiary,
            cursor: "pointer",
            borderRadius: tokens.borderRadius.sm,
            padding: tokens.spacing.xs,
            flexShrink: 0,
            transition: tokens.animation.fast,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
});

// ─── Provider ────────────────────────────────────────────────────────────────

export interface ToastProviderProps {
  /** Toast position (default: top-right) */
  position?: ToastPosition;
  /** Maximum visible toasts (default: 5) */
  maxVisible?: number;
  /** Default duration in ms (default: 5000) */
  defaultDuration?: number;
  /** Children */
  children: React.ReactNode;
}

export const ToastProvider = memo(function ToastProvider({
  position = "top-right",
  maxVisible = 5,
  defaultDuration = 5000,
  children,
}: ToastProviderProps): React.ReactElement {
  const { tokens } = useTheme();
  const { isMobile } = useBreakpoint();
  const [toasts, setToasts] = useState<ToastInstance[]>([]);
  const idCounter = useRef(0);

  const toast = useCallback(
    (options: ToastOptions): string => {
      const id = `toast-${++idCounter.current}-${Date.now()}`;
      const instance: ToastInstance = {
        ...options,
        id,
        createdAt: Date.now(),
        duration: options.duration ?? defaultDuration,
      };
      setToasts((prev) => [...prev, instance].slice(-maxVisible * 2)); // Keep buffer
      return id;
    },
    [defaultDuration, maxVisible],
  );

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Position styles
  const inset = isMobile ? tokens.spacing.sm : tokens.spacing.md;
  const positionStyles: Record<ToastPosition, React.CSSProperties> = {
    "top-right": { top: inset, right: inset },
    "top-left": { top: inset, left: inset },
    "bottom-right": { bottom: inset, right: inset },
    "bottom-left": { bottom: inset, left: inset },
    "top-center": { top: inset, left: "50%", transform: "translateX(-50%)" },
    "bottom-center": {
      bottom: inset,
      left: "50%",
      transform: "translateX(-50%)",
    },
  };

  const isBottom = position.startsWith("bottom");
  const visibleToasts = toasts.slice(-maxVisible);

  return (
    <ToastContext.Provider value={{ toast, dismiss, dismissAll }}>
      {children}

      {/* Toast container */}
      {visibleToasts.length > 0 && (
        <div
          aria-live={
            visibleToasts.some(
              (t) => t.status === "critical" || t.status === "serious",
            )
              ? "assertive"
              : "polite"
          }
          aria-atomic="false"
          style={{
            position: "fixed",
            zIndex: 9999,
            display: "flex",
            flexDirection: isBottom ? "column-reverse" : "column",
            gap: tokens.spacing.sm,
            pointerEvents: "none",
            ...positionStyles[position],
          }}
        >
          {visibleToasts.map((t) => (
            <div key={t.id} style={{ pointerEvents: "auto" }}>
              <ToastItem toast={t} onDismiss={dismiss} />
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes zendir-toast-enter {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes zendir-toast-exit {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(20px); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes zendir-toast-enter { from { opacity: 0; } to { opacity: 1; } }
          @keyframes zendir-toast-exit { from { opacity: 1; } to { opacity: 0; } }
        }
      `}</style>
    </ToastContext.Provider>
  );
});

export default ToastProvider;
